import api from "./api-utils";
import { Temporal } from "@js-temporal/polyfill";
import { RCA_LIST_RESULTS_LIMIT } from "@/constants/rca-constants.js";
import { DAYS_365_IN_HOURS } from "@/lib/datetime-utils.js";

/**
 * Shapes returned by downtime API endpoints. Property names follow typical .NET JSON
 * serialization (PascalCase) as consumed elsewhere in this app.
 *
 * @typedef {Object} DowntimeEventResponseDTO
 * @property {number} UniqueId - Surrogate key for the downtime row
 * @property {string} ReportingDate - Event start (ISO date-time string from JSON)
 * @property {string|null|undefined} ClosingDate - Event end when closed; omitted or null while open
 * @property {string|undefined} RelatedAlarm - Human-readable reason / alarm text
 * @property {number|null|undefined} DowntimeHours - Reported duration in hours; null/undefined while open or unset
 * @property {number} IsMechanical - `IsMechanicalStatus` enum from the API (mapped in the UI via downtime-data-utils)
 * @property {string|undefined} Status - Event status from the downtime service
 *
 * Per-asset bucket for list endpoints. The UI does not rely on extra asset fields here;
 * lease, location, vendor, and display asset number are merged from fleet `AssetContext`.
 *
 * @typedef {Object} DowntimeEventAndAssetSummaryResponseDTO
 * @property {number} AssetId - Non-nullable int asset PK (same value as fleet `AssetId`; UI `asset.id` is its string form)
 * @property {DowntimeEventResponseDTO[]} DowntimeEvents - Events for that asset in the requested window
 *
 * Response body for `POST /api/downtimeevent` ({@link getAllDowntimeEvents}).
 *
 * @typedef {Object} GetAllDowntimeEventsResponse
 * @property {DowntimeEventAndAssetSummaryResponseDTO[]} DowntimeEvents - One summary object per asset with events nested under each
 */

/**
 * Fetch all downtime events for a reporting window (fleet list).
 * @param {string} oldestReportingDate - Oldest reporting date as ISO string (`yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`)
 * @param {string} newestReportingDate - Newest reporting date as ISO string (`yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`)
 * @returns {Promise<GetAllDowntimeEventsResponse>} Response shaped like {@link GetAllDowntimeEventsResponse}
 */
export const getAllDowntimeEvents = async (
  oldestReportingDate,
  newestReportingDate
) => {
  const requestObject = {
    StartingReportingDate: oldestReportingDate,
    EndingReportingDate: newestReportingDate,
  };
  const response = await api.post("/api/downtimeevent", requestObject);
  return response.data;
};

/**
 * Fetch downtime since a rolling window for one asset.
 * @param {string|number} assetId - Fleet asset id (string or int); request body field depends on `events-since-by-asset` contract
 * @returns {Promise<Object>} API response (shape depends on `eventssincebyasset` contract)
 */
export const get12MonthEventsForAsset = async (assetId) => {
  const requestObject = {
    AssetIdentifier: assetId,
    StartingReportingDate: Temporal.Now.instant()
      .subtract(DAYS_365_IN_HOURS)
      .toString({ smallestUnit: "millisecond" }),
  };
  const response = await api.post(
    "/api/downtimeevent/events-since-by-asset",
    requestObject
  );
  return response.data;
};

/**
 * This is the shape returned by `POST /api/downtimeevent/by-asset`.
 *
 * @typedef {Object} ByAssetDowntimeEventApiDTO
 * @property {number} UniqueId
 * @property {string} ReportingDate
 * @property {string} [RelatedAlarm]
 * @property {number|null|undefined} [DowntimeHours]
 */

/**
 * This is the shape we map to for the UI. It is basially identical to what we receive from the API, but we do the
 * mapping here because unlike the other downtime endpoints that goes to a context that manipulates the data, this
 * goes straight to the UI and we want to create a mapping between API and UI at some point.
 *
 * @typedef {Object} ShutdownListRow
 * @property {number} UniqueId
 * @property {string} ReportingDate
 * @property {string} RelatedAlarm
 * @property {number|null} DowntimeHours
 */

/**
 * Maps one `by-asset` downtime row using only the four fields the API returns today.
 * `ReportingDate` is expected as an ISO string from the API (same as other downtime XT endpoints).
 * @param {ByAssetDowntimeEventApiDTO|null|undefined} raw
 * @returns {ShutdownListRow}
 */
function mapDowntimeEventsToListDto(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      UniqueId: 0,
      ReportingDate: new Date(0).toISOString(),
      RelatedAlarm: "",
      DowntimeHours: null,
    };
  }

  let reportingIso = new Date(0).toISOString();
  if (raw.ReportingDate != null && raw.ReportingDate !== "") {
    const d = new Date(raw.ReportingDate);
    if (!Number.isNaN(d.getTime())) reportingIso = d.toISOString();
  }

  let downtimeHours = null;
  if (raw.DowntimeHours != null && raw.DowntimeHours !== "") {
    const n = Number(raw.DowntimeHours);
    if (!Number.isNaN(n)) downtimeHours = n;
  }

  return {
    UniqueId: Number(raw.UniqueId),
    ReportingDate: reportingIso,
    RelatedAlarm: raw.RelatedAlarm != null ? String(raw.RelatedAlarm) : "",
    DowntimeHours: downtimeHours,
  };
}

/**
 * @typedef {Object} GetAllShutdownEventsForAssetResponse
 * @property {ShutdownListRow[]} events
 */

/**
 * Shutdown list for one asset via `POST /api/downtimeevent/by-asset`.
 * @param {string|number} assetId - Fleet asset id (same as `compressor.id`)
 * @returns {Promise<GetAllShutdownEventsForAssetResponse>}
 */
export const getAllShutdownEventsForAsset = async (assetId) => {
  const response = await api.post("/api/downtimeevent/by-asset", {
    assetIdentifier: Number(assetId),
    resultsLimit: RCA_LIST_RESULTS_LIMIT,
  });
  const body = response?.data;
  const rawEvents = Array.isArray(body?.DowntimeEvents)
    ? body.DowntimeEvents
    : Array.isArray(body?.downtimeEvents)
      ? body.downtimeEvents
      : [];
  return {
    events: rawEvents.map(mapDowntimeEventsToListDto),
  };
};
